// ============================================================
// ScrapSalesTab.jsx  —  Noble Enterprises (tape module)
//
// WHAT'S ON THIS SCREEN
//   • Date range filter, headline figures
//   • Sale list with inline add / edit
//   • "Buyers" button   → manage the buyer list
//   • "Report" button   → month-wise, all buyers or one
//
// SCRAP IS PURE INCOME
// Waste absorbs no cost in the costing chain — the whole input
// cost of a jumbo loads onto the good cores it produced. So
// there is nothing to net against this revenue. Every rupee
// here drops straight to the P&L.
//
// WHY BUYERS ARE A TABLE, NOT A TEXT FIELD
// Scrap goes to the same two or three people month after month.
// A typed name would spell three ways and the month-wise report
// would show three buyers where there is one.
//
// NO SEPARATE REPORTS TAB — the report is a button here, next
// to the data it summarises.
// ============================================================

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Plus, Pencil, Trash2, X, Check, Users, BarChart3 } from 'lucide-react';
import WindowModal, { DEFAULT_FROM_DATE, todayISO } from '../components/WindowModal';
import PrintButton from '../print/PrintButton';
import { scrapReportConfig } from './reports/scrapReport.config';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const C = {
  brand:      '#f39c12',
  text:       '#1a2332',
  textSoft:   '#64748b',
  textMuted:  '#94a3b8',
  border:     '#e2e8f0',
  borderSoft: '#f1f5f9',
  card:       '#ffffff',
  rowAlt:     '#fafbfc',
  danger:     '#dc2626',
  dangerBg:   '#fee2e2',
  success:    '#059669',
  successBg:  '#d1fae5',
  info:       '#0284c7',
  infoBg:     '#e0f2fe',
};

const money = (n) =>
  `Rs ${Number(n || 0).toLocaleString('en-PK', { maximumFractionDigits: 0 })}`;
const kg      = (n) => `${Number(n || 0).toLocaleString('en-PK', { maximumFractionDigits: 2 })} kg`;
const fmtDate = (d) => new Date(d).toLocaleDateString('en-GB');


// ============================================================
// SHARED UI
// ============================================================

const inputStyle = {
  width: '100%', padding: '10px 13px',
  border: `1px solid ${C.border}`, borderRadius: '8px',
  fontSize: '14px', color: C.text, boxSizing: 'border-box',
  fontFamily: 'inherit', background: '#ffffff',
};

const Field = ({ label, required, hint, children }) => (
  <div>
    <label style={{
      display: 'block', fontSize: '12px', fontWeight: '600',
      color: C.textSoft, marginBottom: '6px',
    }}>
      {label}
      {required && <span style={{ color: C.danger, marginLeft: '3px' }}>*</span>}
    </label>
    {children}
    {hint && <div style={{ fontSize: '11px', color: C.textMuted, marginTop: '5px' }}>{hint}</div>}
  </div>
);

const TextInput = (props) => <input style={inputStyle} {...props} />;

const Btn = ({ variant = 'primary', children, ...props }) => {
  const styles = {
    primary: { background: C.brand,   color: '#ffffff',  border: 'none' },
    ghost:   { background: '#ffffff', color: C.textSoft, border: `1px solid ${C.border}` },
  };
  return (
    <button
      style={{
        ...styles[variant],
        padding: '10px 18px', borderRadius: '8px', fontSize: '14px',
        fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit',
        display: 'inline-flex', alignItems: 'center', gap: '7px',
      }}
      {...props}
    >
      {children}
    </button>
  );
};

const IconBtn = ({ danger, disabled, title, children, ...props }) => (
  <button
    title={title}
    disabled={disabled}
    style={{
      width: '32px', height: '32px', borderRadius: '7px',
      border: `1px solid ${C.border}`, background: '#ffffff',
      color: disabled ? C.textMuted : (danger ? C.danger : C.textSoft),
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.45 : 1,
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    }}
    {...props}
  >
    {children}
  </button>
);

const Banner = ({ type, message, onClose }) => {
  if (!message) return null;
  const isError = type === 'error';
  return (
    <div style={{
      background: isError ? C.dangerBg : C.successBg,
      color:      isError ? C.danger   : C.success,
      padding: '11px 16px', borderRadius: '8px', marginBottom: '16px',
      fontSize: '14px', display: 'flex', alignItems: 'center', gap: '10px',
    }}>
      <span style={{ flex: 1 }}>{message}</span>
      <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', display: 'flex' }}>
        <X size={16} />
      </button>
    </div>
  );
};

const thStyle = {
  textAlign: 'left', padding: '11px 20px', fontSize: '12px',
  fontWeight: '600', color: C.textSoft, background: C.rowAlt,
  textTransform: 'uppercase', letterSpacing: '0.3px',
};
const tdStyle = {
  padding: '13px 20px', fontSize: '14px', color: C.text,
  borderTop: `1px solid ${C.borderSoft}`,
};

const StatCards = ({ items }) => (
  <div style={{
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: '14px',
  }}>
    {items.map(([label, value]) => (
      <div key={label} style={{
        background: C.card, border: `1px solid ${C.border}`,
        borderRadius: '10px', padding: '15px 17px',
      }}>
        <div style={{ fontSize: '11px', color: C.textMuted, fontWeight: '600', textTransform: 'uppercase' }}>
          {label}
        </div>
        <div style={{ fontSize: '20px', fontWeight: '700', color: C.text, marginTop: '4px' }}>
          {value}
        </div>
      </div>
    ))}
  </div>
);


// ============================================================
// BUYERS MODAL
//
// Add, rename and deactivate. A buyer with sales behind them
// cannot be deleted — the sales would be orphaned and the
// month-wise report would lose a column.
// ============================================================

const BuyersModal = ({ onClose, onChanged, notify }) => {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm]       = useState(null);
  const [busy, setBusy]       = useState(false);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/tape/finance/scrap-buyers`, { headers });
      setRows(res.data.data || []);
    } catch {
      notify('error', 'Could not load buyers');
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const f = form;
    if (!f.name.trim()) return notify('error', 'Buyer name is required');

    setBusy(true);
    try {
      const payload = {
        name: f.name.trim(),
        phone: f.phone.trim() || null,
        address: f.address.trim() || null,
      };
      if (f.id) {
        await axios.put(`${API}/tape/finance/scrap-buyers/${f.id}`, payload, { headers });
        notify('success', 'Buyer updated');
      } else {
        await axios.post(`${API}/tape/finance/scrap-buyers`, payload, { headers });
        notify('success', 'Buyer added');
      }
      setForm(null);
      load();
      onChanged();
    } catch (err) {
      notify('error', err.response?.data?.message || 'Could not save');
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (row) => {
    try {
      await axios.put(
        `${API}/tape/finance/scrap-buyers/${row.id}`,
        { name: row.name, phone: row.phone, address: row.address, is_active: !row.is_active },
        { headers }
      );
      load();
      onChanged();
    } catch (err) {
      notify('error', err.response?.data?.message || 'Could not update');
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete "${row.name}"?`)) return;
    try {
      await axios.delete(`${API}/tape/finance/scrap-buyers/${row.id}`, { headers });
      notify('success', 'Buyer deleted');
      load();
      onChanged();
    } catch (err) {
      notify('error', err.response?.data?.message || 'Could not delete');
    }
  };

  return (
    <WindowModal id="scrap-buyers" title="Scrap buyers" onClose={onClose} width="760px">
      <div style={{ padding: '18px 22px' }}>
        {!form && (
          <div style={{ marginBottom: '16px' }}>
            <Btn onClick={() => setForm({ id: null, name: '', phone: '', address: '' })}>
              <Plus size={16} /> Add buyer
            </Btn>
          </div>
        )}

        {form && (
          <div style={{
            padding: '16px 18px', background: C.rowAlt, borderRadius: '10px',
            border: `1px solid ${C.border}`, marginBottom: '18px',
          }}>
            <div style={{
              display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: '14px', marginBottom: '14px',
            }}>
              <Field label="Name" required>
                <TextInput
                  autoFocus
                  value={form.name}
                  onChange={e => setForm({ ...form, name: e.target.value })}
                  placeholder="Buyer name"
                />
              </Field>
              <Field label="Phone">
                <TextInput
                  value={form.phone}
                  onChange={e => setForm({ ...form, phone: e.target.value })}
                  placeholder="Optional"
                />
              </Field>
              <Field label="Address">
                <TextInput
                  value={form.address}
                  onChange={e => setForm({ ...form, address: e.target.value })}
                  placeholder="Optional"
                />
              </Field>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <Btn onClick={save} disabled={busy}>
                <Check size={16} /> {busy ? 'Saving…' : 'Save'}
              </Btn>
              <Btn variant="ghost" onClick={() => setForm(null)}>Cancel</Btn>
            </div>
          </div>
        )}

        <div style={{
          border: `1px solid ${C.border}`, borderRadius: '10px',
          overflow: 'hidden',
        }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '640px' }}>
              <thead>
                <tr>
                  <th style={thStyle}>Buyer</th>
                  <th style={thStyle}>Phone</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Sales</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Total kg</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Total value</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan="6" style={{ ...tdStyle, textAlign: 'center', color: C.textMuted, padding: '30px' }}>
                      Loading…
                    </td>
                  </tr>
                )}

                {!loading && rows.map(row => {
                  const inUse = Number(row.sale_count) > 0;
                  return (
                    <tr key={row.id} style={{ opacity: row.is_active ? 1 : 0.55 }}>
                      <td style={{ ...tdStyle, fontWeight: '600' }}>
                        {row.name}
                        {!row.is_active && (
                          <span style={{ fontSize: '11px', color: C.textMuted, marginLeft: '8px' }}>
                            inactive
                          </span>
                        )}
                        {row.address && (
                          <div style={{ fontSize: '11px', color: C.textMuted, fontWeight: '400' }}>
                            {row.address}
                          </div>
                        )}
                      </td>
                      <td style={{ ...tdStyle, color: C.textSoft }}>{row.phone || '—'}</td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>{row.sale_count}</td>
                      <td style={{ ...tdStyle, textAlign: 'right', color: C.textSoft }}>
                        {kg(row.total_kg)}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right', fontWeight: '600' }}>
                        {money(row.total_amount)}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>
                        <div style={{ display: 'inline-flex', gap: '7px' }}>
                          <IconBtn
                            title="Edit"
                            onClick={() => setForm({
                              id: row.id, name: row.name,
                              phone: row.phone || '', address: row.address || '',
                            })}
                          >
                            <Pencil size={15} />
                          </IconBtn>
                          <IconBtn
                            title={row.is_active ? 'Deactivate' : 'Activate'}
                            onClick={() => toggleActive(row)}
                          >
                            {row.is_active ? <X size={15} /> : <Check size={15} />}
                          </IconBtn>
                          <IconBtn
                            danger
                            disabled={inUse}
                            title={inUse ? 'Has sales — deactivate instead' : 'Delete'}
                            onClick={() => remove(row)}
                          >
                            <Trash2 size={15} />
                          </IconBtn>
                        </div>
                      </td>
                    </tr>
                  );
                })}

                {!loading && rows.length === 0 && (
                  <tr>
                    <td colSpan="6" style={{ ...tdStyle, textAlign: 'center', color: C.textMuted, padding: '36px' }}>
                      No buyers yet. Add one before recording a scrap sale.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </WindowModal>
  );
};


// ============================================================
// REPORT MODAL
//
// Month by month, either across every buyer or filtered to one.
// Scrap goes out in small lots; a single sale means nothing and
// a month means something.
// ============================================================

const ScrapReportModal = ({ buyers, onClose, notify }) => {
  const [data, setData] = useState({ months: [], buyers: [], totals: {} });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    from_date: DEFAULT_FROM_DATE,
    to_date:   todayISO(),
    buyer_id:  '',
  });

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.from_date) params.append('from_date', filters.from_date);
      if (filters.to_date)   params.append('to_date', filters.to_date);
      if (filters.buyer_id)  params.append('buyer_id', filters.buyer_id);

      const res = await axios.get(
        `${API}/tape/finance/scrap-sales/report?${params}`, { headers }
      );
      setData(res.data.data || { months: [], buyers: [], totals: {} });
    } catch {
      notify('error', 'Could not load the report');
    } finally {
      setLoading(false);
    }
  }, [filters]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const t = data.totals || {};
  const selectedBuyer = buyers.find(b => String(b.id) === String(filters.buyer_id));

  // The report endpoint returns aggregates only, so the sale
  // detail section is fetched at print time rather than kept
  // in state for a screen that never shows it.
  const buildConfig = async () => {
    const params = new URLSearchParams();
    if (filters.from_date) params.append('from_date', filters.from_date);
    if (filters.to_date)   params.append('to_date', filters.to_date);
    if (filters.buyer_id)  params.append('buyer_id', filters.buyer_id);

    const res = await axios.get(`${API}/tape/finance/scrap-sales?${params}`, { headers });

    return scrapReportConfig({
      report: data,
      sales: res.data.data?.rows || [],
      filters,
      buyers,
    });
  };

  return (
    <WindowModal
      id="scrap-report"
      title={selectedBuyer ? `Scrap report — ${selectedBuyer.name}` : 'Scrap report — all buyers'}
      onClose={onClose}
      width="860px"
    >
      <div style={{ padding: '18px 22px' }}>

        <div style={{
          display: 'flex', gap: '12px', flexWrap: 'wrap',
          alignItems: 'flex-end', marginBottom: '18px',
        }}>
          <div style={{ width: '150px' }}>
            <Field label="From">
              <TextInput
                type="date"
                value={filters.from_date}
                onChange={e => setFilters({ ...filters, from_date: e.target.value })}
              />
            </Field>
          </div>
          <div style={{ width: '150px' }}>
            <Field label="To">
              <TextInput
                type="date"
                value={filters.to_date}
                onChange={e => setFilters({ ...filters, to_date: e.target.value })}
              />
            </Field>
          </div>
         <div style={{ width: '200px' }}>
            <Field label="Buyer">
              <select
                style={inputStyle}
                value={filters.buyer_id}
                onChange={e => setFilters({ ...filters, buyer_id: e.target.value })}
              >
                <option value="">All buyers</option>
                {buyers.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </Field>
          </div>

          <div style={{ marginLeft: 'auto' }}>
            <PrintButton
              label="Print Report"
              filename={`Scrap-Report-${filters.from_date}-to-${filters.to_date}`}
              disabled={loading || (data.months || []).length === 0}
              getConfig={buildConfig}
            />
          </div>
        </div>

        <div style={{ marginBottom: '18px' }}>
          <StatCards items={[
            ['Sales',      t.sale_count || 0],
            ['Total kg',   kg(t.total_kg)],
            ['Total value', money(t.total_amount)],
            ['Avg rate',   t.avg_rate ? `Rs ${Number(t.avg_rate).toFixed(0)}/kg` : '—'],
          ]} />
        </div>

        {loading && (
          <p style={{ color: C.textMuted, fontSize: '13px', textAlign: 'center', padding: '24px' }}>
            Loading…
          </p>
        )}

        {/* Month by month */}
        {!loading && (
          <div style={{
            border: `1px solid ${C.border}`, borderRadius: '10px',
            overflow: 'hidden', marginBottom: '18px',
          }}>
            <div style={{
              padding: '12px 20px', background: C.rowAlt,
              fontSize: '12px', fontWeight: '600', color: C.textSoft,
              textTransform: 'uppercase', letterSpacing: '0.3px',
              borderBottom: `1px solid ${C.borderSoft}`,
            }}>
              Month by month
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '520px' }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Month</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Sales</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Weight</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Avg rate</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Value</th>
                  </tr>
                </thead>
                <tbody>
                  {data.months.map(m => (
                    <tr key={m.month_key}>
                      <td style={{ ...tdStyle, fontWeight: '600' }}>{m.month_label}</td>
                      <td style={{ ...tdStyle, textAlign: 'right', color: C.textSoft }}>
                        {m.sale_count}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>{kg(m.total_kg)}</td>
                      <td style={{ ...tdStyle, textAlign: 'right', color: C.textSoft }}>
                        {m.avg_rate ? `Rs ${Number(m.avg_rate).toFixed(2)}` : '—'}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right', fontWeight: '700' }}>
                        {money(m.total_amount)}
                      </td>
                    </tr>
                  ))}
                  {data.months.length === 0 && (
                    <tr>
                      <td colSpan="5" style={{ ...tdStyle, textAlign: 'center', color: C.textMuted, padding: '30px' }}>
                        No scrap sales in this period.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Per buyer — only meaningful when looking at everyone */}
        {!loading && !filters.buyer_id && data.buyers.length > 0 && (
          <div style={{ border: `1px solid ${C.border}`, borderRadius: '10px', overflow: 'hidden' }}>
            <div style={{
              padding: '12px 20px', background: C.rowAlt,
              fontSize: '12px', fontWeight: '600', color: C.textSoft,
              textTransform: 'uppercase', letterSpacing: '0.3px',
              borderBottom: `1px solid ${C.borderSoft}`,
            }}>
              By buyer
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '580px' }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Buyer</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Sales</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Weight</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Avg rate</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Value</th>
                    <th style={thStyle}>Last sale</th>
                  </tr>
                </thead>
                <tbody>
                  {data.buyers.map(b => (
                    <tr key={b.id}>
                      <td style={{ ...tdStyle, fontWeight: '600' }}>{b.buyer_name}</td>
                      <td style={{ ...tdStyle, textAlign: 'right', color: C.textSoft }}>
                        {b.sale_count}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>{kg(b.total_kg)}</td>
                      <td style={{ ...tdStyle, textAlign: 'right', color: C.textSoft }}>
                        {b.avg_rate ? `Rs ${Number(b.avg_rate).toFixed(2)}` : '—'}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right', fontWeight: '700' }}>
                        {money(b.total_amount)}
                      </td>
                      <td style={{ ...tdStyle, color: C.textSoft }}>
                        {b.last_sale_date ? fmtDate(b.last_sale_date) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </WindowModal>
  );
};


// ============================================================
// SALE FORM
// ============================================================

const emptyForm = {
  id: null,
  buyer_id: '',
  sale_date: todayISO(),
  weight_kg: '',
  rate_per_kg: '',
  receipt_no: '',
    notes: '',
  orig_weight_kg: 0,
};

const SaleForm = ({ form, setForm, buyers, onSave, onCancel, saving, inHand }) => {
  const amount = Number(form.weight_kg || 0) * Number(form.rate_per_kg || 0);
  // Editing a sale gives its own weight back before comparing.
  const available = inHand == null ? null : Number(inHand) + Number(form.orig_weight_kg || 0);
  const over = available != null && Number(form.weight_kg || 0) > available;

  return (
    <div style={{
      padding: '20px 22px', background: C.rowAlt,
      borderBottom: `1px solid ${C.borderSoft}`,
    }}>
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: '16px', marginBottom: '16px',
      }}>
        <Field label="Buyer" required>
          <select
            style={inputStyle}
            value={form.buyer_id}
            onChange={e => setForm({ ...form, buyer_id: e.target.value })}
          >
            <option value="">Select…</option>
            {buyers.filter(b => b.is_active || String(b.id) === String(form.buyer_id))
                   .map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </Field>

        <Field label="Date" required>
          <TextInput
            type="date"
            max={todayISO()}
            value={form.sale_date}
            onChange={e => setForm({ ...form, sale_date: e.target.value })}
          />
        </Field>

        <Field label="Weight (kg)" required>
          <TextInput
            type="number" step="0.01" min="0"
            value={form.weight_kg}
            onChange={e => setForm({ ...form, weight_kg: e.target.value })}
            placeholder="0"
          />
        </Field>

        <Field label="Rate per kg" required>
          <TextInput
            type="number" step="0.01" min="0"
            value={form.rate_per_kg}
            onChange={e => setForm({ ...form, rate_per_kg: e.target.value })}
            placeholder="0"
          />
        </Field>

        <Field label="Receipt no.">
          <TextInput
            value={form.receipt_no}
            onChange={e => setForm({ ...form, receipt_no: e.target.value })}
            placeholder="Optional"
          />
        </Field>

        <Field label="Notes">
          <TextInput
            value={form.notes}
            onChange={e => setForm({ ...form, notes: e.target.value })}
            placeholder="Optional"
          />
        </Field>
      </div>

      {amount > 0 && (
        <div style={{
          padding: '13px 17px', borderRadius: '8px', marginBottom: '16px',
          background: '#ffffff', border: `1px solid ${C.border}`,
          display: 'flex', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap',
        }}>
          <span style={{ fontSize: '13px', fontWeight: '600', color: C.textSoft }}>
            Sale value
          </span>
          <span style={{ fontSize: '20px', fontWeight: '700', color: C.text }}>
            {money(amount)}
          </span>
          <span style={{ fontSize: '12px', color: C.textMuted }}>
            income — scrap carries no cost against it
          </span>
        </div>
      )}

            {over && (
        <div style={{
          padding: '11px 14px', borderRadius: '8px', marginBottom: '16px',
          background: '#fef3c7', color: '#92400e', fontSize: '13px',
        }}>
          Only {kg(available)} of scrap is in hand. You can still save — for
          example, scrap from before the system — but check the weight.
        </div>
      )}

      <div style={{ display: 'flex', gap: '10px' }}>
        <Btn onClick={onSave} disabled={saving}>
          <Check size={16} /> {saving ? 'Saving…' : (form.id ? 'Update sale' : 'Save sale')}
        </Btn>
        <Btn variant="ghost" onClick={onCancel}>Cancel</Btn>
      </div>
    </div>
  );
};


// ============================================================
// SCRAP RECEIVED
//
// Where the scrap in hand came from: trim off every core batch,
// and the leftover weighed when a roll is closed. In hand is
// all-time (generated − sold); the list follows the date filter.
// ============================================================

const SOURCE_LABEL = { trim: 'Trim (production)', closing: 'Roll closed' };

const ScrapReceived = ({ stock, loading }) => {
  const entries = stock?.entries || [];
  return (
    <div style={{
      background: C.card, border: `1px solid ${C.border}`,
      borderRadius: '12px', overflow: 'hidden', marginBottom: '16px',
    }}>
      <div style={{ padding: '16px 22px', borderBottom: `1px solid ${C.borderSoft}` }}>
        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: C.text }}>
          Scrap received
        </h3>
        <p style={{ margin: '3px 0 0', fontSize: '13px', color: C.textSoft }}>
          {stock
            ? `Trim ${kg(stock.trim_kg)} + roll closing ${kg(stock.closing_kg)} − sold ${kg(stock.sold_kg)} = ${kg(stock.in_hand_kg)} in hand`
            : '—'}
        </p>
      </div>

      <div style={{ overflowX: 'auto', maxHeight: '320px', overflowY: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '560px' }}>
          <thead>
            <tr>
              <th style={thStyle}>Date</th>
              <th style={thStyle}>Source</th>
              <th style={thStyle}>Roll</th>
              <th style={thStyle}>Batch</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Weight</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan="5" style={{ ...tdStyle, textAlign: 'center', color: C.textMuted, padding: '28px' }}>
                  Loading…
                </td>
              </tr>
            )}

            {!loading && entries.map(e => (
              <tr key={`${e.source}-${e.id}`}>
                <td style={{ ...tdStyle, color: C.textSoft, whiteSpace: 'nowrap' }}>
                  {e.entry_date ? fmtDate(e.entry_date) : '—'}
                </td>
                <td style={tdStyle}>{SOURCE_LABEL[e.source] || e.source}</td>
                <td style={{ ...tdStyle, fontWeight: '600' }}>{e.roll_number || '—'}</td>
                <td style={{ ...tdStyle, color: C.textSoft }}>{e.ref || '—'}</td>
                <td style={{ ...tdStyle, textAlign: 'right', fontWeight: '600' }}>
                  {kg(e.weight_kg)}
                </td>
              </tr>
            ))}

            {!loading && entries.length === 0 && (
              <tr>
                <td colSpan="5" style={{ ...tdStyle, textAlign: 'center', color: C.textMuted, padding: '32px' }}>
                  No scrap received in this period.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};


// ============================================================
// MAIN COMPONENT
// =============================================================

const ScrapSalesTab = () => {
  const [data, setData]       = useState({ rows: [], totals: {} });
  const [buyers, setBuyers]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm]       = useState(null);
  const [saving, setSaving]   = useState(false);
  const [banner, setBanner]   = useState({ type: '', message: '' });

    const [stock, setStock]           = useState(null);
  const [showBuyers, setShowBuyers] = useState(false);
  const [showReport, setShowReport] = useState(false);

  const [filters, setFilters] = useState({
    from_date: DEFAULT_FROM_DATE,
    to_date:   todayISO(),
    buyer_id:  '',
  });

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const notify = (type, message) => {
    setBanner({ type, message });
    if (type === 'success') setTimeout(() => setBanner({ type: '', message: '' }), 3000);
  };

  const loadBuyers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/tape/finance/scrap-buyers`, { headers });
      setBuyers(res.data.data || []);
    } catch {
      /* the sale list will still load; the dropdown just stays empty */
    }
  }, []); // eslint-disable-line

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.from_date) params.append('from_date', filters.from_date);
      if (filters.to_date)   params.append('to_date', filters.to_date);
      if (filters.buyer_id)  params.append('buyer_id', filters.buyer_id);

            const [res, st] = await Promise.all([
        axios.get(`${API}/tape/finance/scrap-sales?${params}`, { headers }),
        axios.get(`${API}/tape/finance/scrap-stock?${params}`, { headers }),
      ]);
      setData(res.data.data || { rows: [], totals: {} });
      setStock(st.data.data || null);
    } catch {
      notify('error', 'Could not load scrap sales. Is the backend running?');
    } finally {
      setLoading(false);
    }
  }, [filters]); // eslint-disable-line

  useEffect(() => { loadBuyers(); }, [loadBuyers]);
  useEffect(() => { load(); }, [load]);

  const openAdd = () => {
    if (buyers.filter(b => b.is_active).length === 0) {
      setShowBuyers(true);
      return notify('error', 'Add a buyer first');
    }
    setForm(emptyForm);
  };

  const openEdit = (row) => setForm({
    id: row.id,
    buyer_id: String(row.buyer_id),
    sale_date: String(row.sale_date).split('T')[0],
    weight_kg: row.weight_kg,
    rate_per_kg: row.rate_per_kg,
    receipt_no: row.receipt_no || '',
        notes: row.notes || '',
    orig_weight_kg: row.weight_kg,
  });

  const save = async () => {
    const f = form;
    if (!f.buyer_id)                   return notify('error', 'Pick a buyer');
    if (!f.sale_date)                  return notify('error', 'Date is required');
    if (!(Number(f.weight_kg) > 0))    return notify('error', 'Weight must be greater than 0');
    if (!(Number(f.rate_per_kg) >= 0)) return notify('error', 'Rate cannot be negative');

    setSaving(true);
    try {
      const payload = {
        buyer_id:    f.buyer_id,
        sale_date:   f.sale_date,
        weight_kg:   f.weight_kg,
        rate_per_kg: f.rate_per_kg,
        receipt_no:  f.receipt_no,
        notes:       f.notes,
      };

      if (f.id) {
        await axios.put(`${API}/tape/finance/scrap-sales/${f.id}`, payload, { headers });
        notify('success', 'Sale updated');
      } else {
        await axios.post(`${API}/tape/finance/scrap-sales`, payload, { headers });
        notify('success', 'Scrap sale recorded');
      }

      setForm(null);
      load();
      loadBuyers();
    } catch (err) {
      notify('error', err.response?.data?.message || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete this ${kg(row.weight_kg)} sale to ${row.buyer_name}?`)) return;
    try {
      await axios.delete(`${API}/tape/finance/scrap-sales/${row.id}`, { headers });
      notify('success', 'Sale deleted');
      load();
      loadBuyers();
    } catch (err) {
      notify('error', err.response?.data?.message || 'Could not delete');
    }
  };

  const t = data.totals || {};

  return (
    <div>
      <Banner
        type={banner.type}
        message={banner.message}
        onClose={() => setBanner({ type: '', message: '' })}
      />

      <div style={{ marginBottom: '16px' }}>
                <StatCards items={[
          ['Scrap in hand', stock ? kg(stock.in_hand_kg) : '—'],
          ['Sales',       t.sale_count || 0],
          ['Weight sold', kg(t.total_kg)],
          ['Income',      money(t.total_amount)],
          ['Avg rate',    t.avg_rate ? `Rs ${Number(t.avg_rate).toFixed(2)}/kg` : '—'],
        ]} />
      </div>

            <ScrapReceived stock={stock} loading={loading} />

      <div style={{
        background: C.card, border: `1px solid ${C.border}`,
        borderRadius: '12px', overflow: 'hidden',
      }}>
        <div style={{
          padding: '18px 22px', borderBottom: `1px solid ${C.borderSoft}`,
          display: 'flex', alignItems: 'flex-end', gap: '12px', flexWrap: 'wrap',
        }}>
          <div style={{ flex: '1 1 180px', minWidth: '160px' }}>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: C.text }}>
              Scrap sales
            </h3>
            <p style={{ margin: '3px 0 0', fontSize: '13px', color: C.textSoft }}>
              Waste sold on. Straight income — the cost already sat with the good cores.
            </p>
          </div>

          <div style={{ width: '148px' }}>
            <Field label="From">
              <TextInput
                type="date"
                value={filters.from_date}
                onChange={e => setFilters({ ...filters, from_date: e.target.value })}
              />
            </Field>
          </div>

          <div style={{ width: '148px' }}>
            <Field label="To">
              <TextInput
                type="date"
                value={filters.to_date}
                onChange={e => setFilters({ ...filters, to_date: e.target.value })}
              />
            </Field>
          </div>

          <div style={{ width: '170px' }}>
            <Field label="Buyer">
              <select
                style={inputStyle}
                value={filters.buyer_id}
                onChange={e => setFilters({ ...filters, buyer_id: e.target.value })}
              >
                <option value="">All buyers</option>
                {buyers.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </Field>
          </div>

          <Btn variant="ghost" onClick={() => setShowBuyers(true)}>
            <Users size={16} /> Buyers
          </Btn>

          <Btn variant="ghost" onClick={() => setShowReport(true)}>
            <BarChart3 size={16} /> Report
          </Btn>

          {!form && (
            <Btn onClick={openAdd}><Plus size={16} /> Record sale</Btn>
          )}
        </div>

        {form && (
          <SaleForm
            form={form}
            setForm={setForm}
                       buyers={buyers}
            inHand={stock ? stock.in_hand_kg : null}
            onSave={save}
            onCancel={() => setForm(null)}
            saving={saving}
          />
        )}

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '760px' }}>
            <thead>
              <tr>
                <th style={thStyle}>Date</th>
                <th style={thStyle}>Buyer</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Weight</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Rate</th>
                <th style={thStyle}>Receipt</th>
                <th style={thStyle}>Notes</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Amount</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan="8" style={{ ...tdStyle, textAlign: 'center', color: C.textMuted, padding: '36px' }}>
                    Loading…
                  </td>
                </tr>
              )}

              {!loading && data.rows.map(row => (
                <tr key={row.id}>
                  <td style={{ ...tdStyle, color: C.textSoft, whiteSpace: 'nowrap' }}>
                    {fmtDate(row.sale_date)}
                  </td>
                  <td style={{ ...tdStyle, fontWeight: '600' }}>
                    {row.buyer_name}
                    {row.buyer_phone && (
                      <div style={{ fontSize: '11px', color: C.textMuted, fontWeight: '400' }}>
                        {row.buyer_phone}
                      </div>
                    )}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{kg(row.weight_kg)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', color: C.textSoft }}>
                    Rs {Number(row.rate_per_kg).toFixed(2)}
                  </td>
                  <td style={{ ...tdStyle, color: C.textSoft }}>{row.receipt_no || '—'}</td>
                  <td style={{
                    ...tdStyle, color: C.textSoft, maxWidth: '180px',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {row.notes || '—'}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'right', fontWeight: '700' }}>
                    {money(row.amount)}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>
                    <div style={{ display: 'inline-flex', gap: '7px' }}>
                      <IconBtn title="Edit" onClick={() => openEdit(row)}>
                        <Pencil size={15} />
                      </IconBtn>
                      <IconBtn danger title="Delete" onClick={() => remove(row)}>
                        <Trash2 size={15} />
                      </IconBtn>
                    </div>
                  </td>
                </tr>
              ))}

              {!loading && data.rows.length === 0 && (
                <tr>
                  <td colSpan="8" style={{ ...tdStyle, textAlign: 'center', color: C.textMuted, padding: '40px' }}>
                    No scrap sales in this period.
                  </td>
                </tr>
              )}
            </tbody>

            {!loading && data.rows.length > 0 && (
              <tfoot>
                <tr>
                  <td colSpan="6" style={{
                    ...tdStyle, textAlign: 'right', fontWeight: '600',
                    color: C.textSoft, background: C.rowAlt,
                  }}>
                    Total
                  </td>
                  <td style={{
                    ...tdStyle, textAlign: 'right', fontWeight: '700',
                    fontSize: '15px', background: C.rowAlt,
                  }}>
                    {money(t.total_amount)}
                  </td>
                  <td style={{ ...tdStyle, background: C.rowAlt }} />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {showBuyers && (
        <BuyersModal
          notify={notify}
          onChanged={() => { loadBuyers(); load(); }}
          onClose={() => setShowBuyers(false)}
        />
      )}

      {showReport && (
        <ScrapReportModal
          buyers={buyers}
          notify={notify}
          onClose={() => setShowReport(false)}
        />
      )}
    </div>
  );
};

export default ScrapSalesTab;